import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { CustomPaginator } from '../../../components/CustomPaginator/CustomPaginator';
import { TicketFilters } from './TicketFilters';
import { TicketsListSkeleton } from './TicketsListSkeleton';
import { TicketTypesLegend } from './TicketTypesLegend';
import { useClient } from '../../../hooks/useClient';
import { useAuthContext } from '../../../context/AuthContext';
import { TicketListItem } from '../../../types/ticket';
import { PaginatedResponse } from '../../../types/serverTable';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { PrimeIcons } from 'primereact/api';
import { TICKET_STATUS_LABELS, TICKET_SEVERITY_LABELS } from '../constants/ticketLabels';
import { useTicketFilters } from '../hooks/useTicketFilters';
import { useUnreadTickets } from '../hooks/useUnreadTickets';
import './TicketsList.scss';

const STATUS_COLORS: Record<string, string> = {
  'To Do': 'status-todo',
  'In Progress': 'status-in-progress',
  QA: 'status-qa',
  Done: 'status-done',
};

const STATUS_ICONS: Record<string, string> = {
  'To Do': PrimeIcons.CLOCK,
  'In Progress': PrimeIcons.SPINNER,
  QA: PrimeIcons.EYE,
  Done: PrimeIcons.CHECK_CIRCLE,
};

const TYPE_ICONS: Record<string, string> = {
  bug: PrimeIcons.TIMES_CIRCLE,
  feature: PrimeIcons.STAR,
  modification: PrimeIcons.COG,
};

const TYPE_COLORS: Record<string, string> = {
  bug: 'var(--danger)',
  feature: 'var(--success)',
  modification: 'var(--info)',
};

const SEVERITY_COLORS: Record<string, string> = {
  low: 'severity-low',
  medium: 'severity-medium',
  high: 'severity-high',
  critical: 'severity-critical',
};

export const TicketsList = () => {
  const { get } = useClient();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Usar el hook personalizado para los filtros
  const filters = useTicketFilters(500);
  const { isUnread } = useUnreadTickets();

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setPage(0);
  }, [filters.debouncedTitle, filters.type, filters.severity]);

  const url = useMemo(() => {
    if (!user?.organizationId) return null;
    const params = new URLSearchParams();
    params.append('page', (page + 1).toString());
    params.append('pageSize', pageSize.toString());

    if (filters.debouncedTitle.trim()) {
      params.append('title', filters.debouncedTitle.trim());
    }

    if (filters.type.length > 0) {
      params.append('type', filters.type.join(','));
    }

    if (filters.severity.length > 0) {
      params.append('severity', filters.severity.join(','));
    }

    return `/organizations/${user.organizationId}/tickets?${params.toString()}`;
  }, [user?.organizationId, page, pageSize, filters.debouncedTitle, filters.type, filters.severity]);
  
  interface TicketsResponse {
    cards: TicketListItem[];
    pagination: PaginatedResponse<TicketListItem>['pagination'];
  }

  const { data, isLoading } = useSWR<TicketsResponse>(
    url,
    async (url: string) => {
      const response = await get<TicketsResponse>(url);
      if (!response) throw new Error('Failed to fetch tickets');
      return response;
    },
    {
      revalidateOnFocus: false,
    },
  );

  const handleTicketClick = useCallback((cardId: string) => {
    navigate(`/support/ticket/${cardId}`);
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number, newPageSize?: number) => {
    setPage(newPage);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
  }, [pageSize]);

  const handleClearFilters = useCallback(() => {
    filters.clearFilters();
    setPage(0);
  }, [filters]);

  const getStatusBadge = useCallback((status: string) => {
    const statusLabel = TICKET_STATUS_LABELS[status as keyof typeof TICKET_STATUS_LABELS] || status;
    return (
      <span className={classNames('status-badge', STATUS_COLORS[status] || 'status-default')}>
        <i className={classNames(STATUS_ICONS[status] || PrimeIcons.CIRCLE, {
          'pi-spin': status === 'In Progress',
        })}></i>
        <span className='status-badge-text'>{statusLabel}</span>
      </span>
    );
  }, []);

  const tickets = data?.cards ?? [];
  const pagination = data?.pagination;

  return (
    <Card className='tickets-list-card'>
      <div className='card-header'>
        <i className='pi pi-list card-icon'></i>
        <h4>Tickets de soporte</h4>
      </div>
      <Divider />
      <TicketTypesLegend />
      <TicketFilters
        title={filters.title}
        type={filters.type}
        severity={filters.severity}
        onTitleChange={filters.setTitle}
        onTypeChange={filters.setType}
        onSeverityChange={filters.setSeverity}
        onClearFilters={handleClearFilters}
      />
      {isLoading ? (
        <TicketsListSkeleton />
      ) : tickets.length === 0 ? (
        <p className='no-tickets'>
          {filters.hasActiveFilters
            ? 'No se encontraron tickets con los filtros aplicados.'
            : 'No tienes tickets creados todavía.'}
        </p>
      ) : (
        <>
          <div className='tickets-list'>
            {tickets.map((ticket) => (
              <div key={ticket.cardId} className='ticket-item' onClick={() => handleTicketClick(ticket.cardId)}>
                <div className='ticket-header'>
                  <div className='ticket-title'>
                    {isUnread(ticket.cardId) && <span className='unread-dot'></span>}
                    {ticket.type && TYPE_ICONS[ticket.type] && (
                      <i
                        className={classNames('ticket-type-icon', TYPE_ICONS[ticket.type])}
                        style={{ color: TYPE_COLORS[ticket.type] }}
                      ></i>
                    )}
                    <span className='ticket-title-text'>{ticket.title}</span>
                  </div>
                  {getStatusBadge(ticket.status)}
                </div>
                <div className='ticket-meta'>
                  {ticket.severity && (
                    <span className={classNames('meta-badge', SEVERITY_COLORS[ticket.severity] || 'severity-default')}>
                      Severidad: {TICKET_SEVERITY_LABELS[ticket.severity]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {pagination && (
            <CustomPaginator
              page={page}
              pageSize={pageSize}
              totalRecords={pagination.totalRecords}
              onPageChange={handlePageChange}
              pageSizeOptions={[10, 25, 50]}
            />
          )}
        </>
      )}
    </Card>
  );
};
